import { memo, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { Box, useDialogController } from '@machobear/design-system'
import posthog from 'posthog-js'

import { useFilters } from '@/hooks/use-filters'
import useSessionComplete from '@/hooks/use-session-complete'
import useSetTrrebDlv from '@/hooks/use-set-trreb-dlv'
import { BuildingListModal } from '@/features/Listings/map/building-list-modal'
import GoogleAdSenseCard from '@/components/adsense'
import SkeletonCard from '@/components/listing-card-new/SkeletonCard'
import { Listing } from '@/components/listing-card-new/types'
import {
  getClickEventId,
  getListingCardDlv
} from '@/components/listing-card/utils'
import { useTermsDialogContext } from '@/components/terms-dialog/context'
import { Label } from '@/components/text'

import { ListingCardItem } from './listing-card-item-v2'

interface RegularListingsProps {
  loading: boolean
  listings: Listing[]
  setHighlightedBuilding: (buildingId: string) => void
  setVisitedBuilding: (building: Listing) => void
  hideFeaturedListings: boolean
  onlyVerifiedListings: boolean
  isMobile: boolean
}

const RegularListings = memo(
  ({
    loading,
    listings = [],
    setHighlightedBuilding,
    setVisitedBuilding,
    hideFeaturedListings,
    onlyVerifiedListings,
    isMobile
  }: RegularListingsProps) => {
    const { t } = useTranslation()
    const router = useRouter()
    const hasSession = useSessionComplete()
    const [filters] = useFilters()
    const { visible, showModal, closeModal } = useDialogController()
    const [buildingData, setBuildingData] = useState<Listing | null>(null)

    const { isLoading, checkAgreementMismatch } = useTermsDialogContext()
    const isAgreementMismatched = checkAgreementMismatch('trreb_agreements')
    const isAgreementLoading = isLoading && hasSession

    useSetTrrebDlv(isAgreementMismatched)

    const buildingFilters = onlyVerifiedListings
      ? { ...filters, search_only_verified: true }
      : filters

    const titleText = t('b.listings.view.most_relevant.text', 'Most Relevant')
    const showSkeletonState =
      (listings.length === 0 && loading) || (hasSession && isAgreementLoading)

    const nonSuperBoostedFeaturedCount = { current: 0 }

    const renderItem = (
      listing: Listing,
      nonSuperBoostedFeaturedCount: { current: number }
    ) => {
      if (!listing) return null

      if (listing.__typename === 'AdSense') {
        return <GoogleAdSenseCard key={listing.id} positionId={listing.id} />
      }

      const isBuilding = listing.listing_count && listing.listing_count > 1

      // Super boosted listings show badge on all cards
      // Non-super boosted listings only show badge on first 3 cards
      const superBoosted = (listing.super_boost ?? 0) > 1
      let hideFeaturedBadge = false

      if (superBoosted) {
        hideFeaturedBadge = false
      } else if (
        listing.sponsored &&
        nonSuperBoostedFeaturedCount.current < 3
      ) {
        hideFeaturedBadge = false
        nonSuperBoostedFeaturedCount.current++
      } else {
        hideFeaturedBadge = true
      }

      return (
        <ListingCardItem
          id={getClickEventId(listing, false, '')}
          key={
            listing.listing_id
              ? `listing-${listing.listing_id}`
              : `building-${listing.building_id}`
          }
          listing={listing}
          isFeatured={listing.sponsored}
          hideFeaturedBadge={hideFeaturedBadge}
          isV2ListingCard={true}
          hasSession={hasSession}
          isAgreementMismatched={isAgreementMismatched}
          isAgreementLoading={isAgreementLoading}
          onListingHover={() => {
            setHighlightedBuilding(listing.building_id)
          }}
          trackImpression={{
            id: listing.listing_id
              ? listing.listing_id
              : listing.listings?.map(l => l.listing_id) || [],
            enabled: true,
            page: 'listings',
            section: isBuilding ? 'building-card' : 'listing-card',
            sponsored: listing.sponsored || false
          }}
          onClick={() => {
            if (isBuilding) {
              setBuildingData(listing)
              showModal()
            } else {
              posthog.capture(
                listing.sponsored
                  ? 'sponsored_listing_card_click'
                  : 'regular_listing_card_click'
              )
              window.dataLayer.push({
                event: getClickEventId(listing, false, ''),
                listingCard: getListingCardDlv(listing, false)
              })
            }
            setVisitedBuilding(listing)
          }}
        />
      )
    }

    const renderSkeletonCards = (count: number) => {
      return Array.from({ length: count }, (_, index) => (
        <SkeletonCard key={`skeleton-${index}`} />
      ))
    }

    return (
      <Box display="flex" flexDirection="column" gap="small">
        {!hideFeaturedListings && <Label fontSize="14px">{titleText}</Label>}
        {isMobile ? (
          <Box
            display="flex"
            flexDirection="column"
            gap="medium"
            width="full"
            style={{
              overflowX: 'hidden'
            }}
          >
            {showSkeletonState
              ? renderSkeletonCards(6)
              : listings.map(listing =>
                  renderItem(listing, nonSuperBoostedFeaturedCount)
                )}
          </Box>
        ) : (
          <Box
            display="grid"
            gap="medium"
            style={{
              gridTemplateColumns: 'repeat(auto-fill, 320px)'
            }}
          >
            {showSkeletonState
              ? renderSkeletonCards(12)
              : listings.map(listing =>
                  renderItem(listing, nonSuperBoostedFeaturedCount)
                )}
          </Box>
        )}
        <BuildingListModal
          id={buildingData?.building_name}
          visible={visible}
          onClose={closeModal}
          building_id={buildingData?.building_id}
          building={buildingData}
          filters={buildingFilters}
          router={router}
          trackImpression={{
            enabled: true,
            page: 'listings',
            section: 'listing-card',
            sponsored: false
          }}
        />
      </Box>
    )
  }
)

RegularListings.displayName = 'RegularListings'

export default RegularListings
